import { useDisclosure } from '@televet/kibble-ui/build/chakra';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';
import omitBy from 'lodash-es/omitBy';
import orderBy from 'lodash-es/orderBy';
import uniqBy from 'lodash-es/uniqBy';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { replaceParam, RouteBasePaths, RouteDefinitions } from 'routes';
import { Mode } from 'shared/components/SidePanel/components/SearchPanel/shared/types/enum/Mode';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import usePersistedState from 'shared/hooks/usePersistedState';
import useUnreadMessageCount from 'shared/hooks/useUnreadMessageCount';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  ChannelListChannelFragment,
  ChannelStatusAction,
  ChannelType as ChannelType,
  FilterSelectionType,
  GetChannelListChannelsQuery,
  SearchChannelInput,
  SortOrder,
  useGetAllClinicChannelStatusesQuery,
  useGetChannelListChannelsQuery,
  useGetPinnedChannelListChannelsQuery,
  useSubscribeToChannelListChannelsSubscription,
} from 'shared/types/graphql';
import { useTeletriageConversationList } from '../components/TeletriageConversationCenter/hooks/useTeletriageConversationsList';
import { ChannelListFilterType } from '../interfaces/ChannelListFilterType';
import { ChannelSearchInput, ChannelSearchInputUpdates } from '../interfaces/ChannelSearchInput';
import { defaultChannelListFilter, IChannelListFilter } from '../interfaces/IChannelListFilter';
import {
  defaultChannelListFilterSelection,
  IChannelListFilterSelection,
} from '../interfaces/IChannelListFilterSelection';
import { QuickSelectOptionValues } from './useChannelFilterOptions';

export const DEFAULT_CHANNEL_LIST_PAGE_SIZE = 20;
export const DEFAULT_PINNED_CHANNEL_LIST_PAGE_SIZE = 100;

export type ConversationsType = {
  channelSearchInput: ChannelSearchInput;
  channelListFilter: IChannelListFilter;
  hasLoadedInitialChannelList: boolean;
  hasNextPage: boolean;
  refetchChannels: ReturnType<typeof useGetChannelListChannelsQuery>['refetch'];
  unpinnedChannels: GetChannelListChannelsQuery['channelSearch']['channels'];
  pinnedChannels: GetChannelListChannelsQuery['channelSearch']['channels'];
  isLoadingChannelList: boolean;
  unreadChannelIds: string[];
  showUnreadEmptyState: boolean;
  clearChannelFilter: (source?: string, ignoreFilterKeys?: (keyof IChannelListFilter)[]) => void;
  isSearching: boolean;
  currentChannelFilterSelection: IChannelListFilterSelection;
  isFilterApplied: boolean;
  handleSearchToggle: (isSearching: boolean) => void;
  handleChannelSearchInputChange: (updates: ChannelSearchInputUpdates) => void;
  handleFilterSelect: (
    filterSelection: Partial<IChannelListFilterSelection>,
    ignoreFilterKeys?: (keyof IChannelListFilter)[],
  ) => void;
  handleChannelListViewSelect: (filterType: ChannelListFilterType) => void;
  handleShowArchivedToggle: () => void;
  channelId: string;
  unreadMessageCountByChannel: Record<string, { count: number }>;
  handleChannelSelect: (channelId: string) => void;
  searchResultsCount: number;
  showSearchResultsCount: boolean;
  showEmptyState: boolean;
  filterPopoverKey: number;
  teletriageConversationCenter: ReturnType<typeof useTeletriageConversationList>;
  isTeletriageConversationCenterOpen: boolean;
  onTeletriageConversationCenterOpen: () => void;
  onTeletriageConversationCenterClose: () => void;
};

export const useConversations = (shouldShowTeletriageConversationCenter: boolean): ConversationsType => {
  const { channelId } = useParams<{ channelId: string }>();
  const [isSearching, setIsSearching] = useState(false);
  const [channelSearchInput, setChannelSearchInput] = useState<ChannelSearchInput>({});
  const [hasLoadedInitialChannelList, setHasLoadedInitialChannelList] = useState(false);
  const { setSearchPanelMode } = useSidePanelSearch();
  const { isFeatureEnabled } = useFeatureFlag();
  const history = useHistory();
  const { clinicUser, clinicUserId, currentClinicId } = useClinicUser();
  const userId = clinicUser?.id || '';
  const clinicKey = `Clinic:${currentClinicId}`;
  const userKey = `User:${userId}`;
  const [filterPopoverKey, setFilterPopoverKey] = useState(Date.now());
  const { trackEvent } = useTracking();
  const isSortConversationsByLastMessageEnabled = isFeatureEnabled(FeatureFlagName.SortConversationByLastMessage);

  const {
    isOpen: isTeletriageConversationCenterOpen,
    onOpen: _onTeletriageConversationCenterOpen,
    onClose: _onTeletriageConversationCenterClose,
  } = useDisclosure({ defaultIsOpen: true });

  const teletriageConversationCenter = useTeletriageConversationList({
    isDrawerOpen: shouldShowTeletriageConversationCenter && isTeletriageConversationCenterOpen,
  });

  const onTeletriageConversationCenterOpen = useCallback(() => {
    _onTeletriageConversationCenterOpen();
    setIsSearching(false);
    teletriageConversationCenter.toggleSearch(false);
  }, [_onTeletriageConversationCenterOpen, teletriageConversationCenter]);

  const onTeletriageConversationCenterClose = useCallback(() => {
    _onTeletriageConversationCenterClose();
    setIsSearching(false);
    teletriageConversationCenter.toggleSearch(false);
  }, [_onTeletriageConversationCenterClose, teletriageConversationCenter]);

  const [channelListFilters, setChannelListFilters] = usePersistedState<
    Record<string, Record<string, IChannelListFilter>> | undefined | null
  >(PersistedStateKeys.ChannelListFilters, { [clinicKey]: { [userKey]: defaultChannelListFilter } });

  const [channelListFilterSelections, setChannelListFilterSelections] = usePersistedState<
    Record<string, Record<string, IChannelListFilterSelection>> | undefined | null
  >(PersistedStateKeys.ChannelListFilterSelections, {
    [clinicKey]: { [userKey]: defaultChannelListFilterSelection },
  });

  // Old conversation filters persisted only to local storage
  const oldChannelListFilter = useMemo(() => {
    if (
      !currentClinicId ||
      !userId ||
      !channelListFilters ||
      !channelListFilters[clinicKey] ||
      !channelListFilters[clinicKey][userKey]
    ) {
      return defaultChannelListFilter;
    }
    return channelListFilters[clinicKey][userKey];
  }, [channelListFilters, currentClinicId, clinicKey, userId, userKey]);

  // New conversation filters persisted to the database
  const currentChannelFilterSelection = useMemo(() => {
    if (
      !currentClinicId ||
      !userId ||
      !channelListFilterSelections ||
      !channelListFilterSelections[clinicKey] ||
      !channelListFilterSelections[clinicKey][userKey]
    ) {
      return defaultChannelListFilterSelection;
    }
    return channelListFilterSelections[clinicKey][userKey];
  }, [channelListFilterSelections, currentClinicId, clinicKey, userId, userKey]);

  const channelListFilter = useMemo(() => {
    if (currentChannelFilterSelection) {
      return currentChannelFilterSelection.filters;
    }
    if (oldChannelListFilter) {
      return oldChannelListFilter;
    }
    return defaultChannelListFilter;
  }, [oldChannelListFilter, currentChannelFilterSelection]);

  const {
    data: { unreadChannelMap: unreadMessageCountByChannel },
  } = useUnreadMessageCount();

  const unreadChannelIds = useMemo(() => {
    const ids = Object.entries(unreadMessageCountByChannel)
      .filter(([, { count }]) => count > 0)
      .map(([id]) => id);
    // We always want to display the current channel
    if (channelId) {
      ids.push(channelId);
    }
    return ids;
  }, [channelId, unreadMessageCountByChannel]);

  const showUnreadEmptyState = useMemo(() => {
    return Boolean(channelListFilter.unread && unreadChannelIds?.length === 0);
  }, [channelListFilter, unreadChannelIds]);

  const { data: channelStatusesData } = useGetAllClinicChannelStatusesQuery({
    variables: { clinicId: currentClinicId || '' },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    skip: !currentClinicId,
  });

  const channelListQueryWhere: SearchChannelInput = useMemo(() => {
    const includeUnassigned =
      channelListFilter?.assigneeIds.filter((option) => option === QuickSelectOptionValues.UNASSIGNED).length > 0;
    const includeAssignedToMe =
      channelListFilter?.assigneeIds.filter((option) => option === QuickSelectOptionValues.ASSIGNED_TO_ME).length > 0;
    const assignees = [...channelListFilter.assigneeIds];

    if (includeAssignedToMe && clinicUser?.id) {
      assignees.push(clinicUser?.id);
    }

    let channelStatusIds = channelListFilter.channelStatusIds.slice();

    if (!channelListFilter.showArchived && !channelListFilter.channelStatusIds.length) {
      channelStatusIds = (channelStatusesData?.findManyChannelStatus || [])
        .filter((channelStatus) => channelStatus.channelStatusAction === ChannelStatusAction.Active)
        .map(({ id }) => id);
    }

    return {
      ...omit(channelListFilter, 'unread', 'assigneeIds', 'showArchived'),
      idIn: channelListFilter.unread ? unreadChannelIds : [],
      ...channelSearchInput,
      channelType: ChannelType.Client,
      clinicId: currentClinicId,
      isActive: true,
      assigneeIds: assignees,
      channelStatusIds,
      isUnassigned: includeUnassigned,
    };
  }, [currentClinicId, channelListFilter, channelSearchInput, unreadChannelIds, clinicUser, channelStatusesData]);

  /**
   * If archived conversations are hidden and no specific statuses are already included in the filter, wait
   * until channel statuses are returned before fetching channels because we need the active status IDs
   * to only fetch active channels.
   */
  const shouldFetchChannels = useMemo(
    () =>
      channelListFilter.showArchived ||
      (!channelListFilter.showArchived && (!!channelListFilter.channelStatusIds.length || !!channelStatusesData)),
    [channelListFilter, channelStatusesData],
  );

  const {
    data: channelsData,
    loading: isLoadingChannelList,
    variables: channelListVariables,
    refetch: refetchChannels,
  } = useGetChannelListChannelsQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !currentClinicId || !shouldFetchChannels,
    variables: {
      where: { ...channelListQueryWhere, excludedChannelPinUserIds: [clinicUserId || ''] },
      take: DEFAULT_CHANNEL_LIST_PAGE_SIZE,
      skip: 0,
    },
    onCompleted: () => {
      if (!hasLoadedInitialChannelList) {
        setHasLoadedInitialChannelList(true);
      }
    },
  });

  const { data: pinnedChannelsData, refetch: refetchPinnedChannels } = useGetPinnedChannelListChannelsQuery({
    skip: !currentClinicId,
    variables: {
      where: {
        channelPinUserIds: [clinicUserId || ''],
        clinicId: currentClinicId,
      },
    },
  });

  const unpinnedChannels = useMemo(() => {
    // Unpinned channels are sorted by createdAt if the sort by last message FF is enabled
    // Otherwise they are sorted by updatedAt
    const sortedChannels = orderBy(
      channelsData?.channelSearch?.channels,
      (channel): Date =>
        isSortConversationsByLastMessageEnabled
          ? channel.lastMessage?.createdAt || channel.updatedAt
          : channel.updatedAt,
      SortOrder.Desc,
    );

    return uniqBy(sortedChannels, 'id') || [];
  }, [channelsData?.channelSearch?.channels, isSortConversationsByLastMessageEnabled]);

  const pinnedChannels = useMemo(() => {
    // Pinned channels are sorted by createdAt date
    const sortedChannels = orderBy(
      pinnedChannelsData?.channelSearch.channels,
      (channel: ChannelListChannelFragment): Date =>
        (channel.channelPins || []).find((cp) => cp.userId === clinicUserId)?.createdAt,
      SortOrder.Asc,
    );

    return uniqBy(sortedChannels, 'id') || [];
  }, [clinicUserId, pinnedChannelsData?.channelSearch.channels]);

  const handleChannelSelect = useCallback(
    (channelId: string) => {
      setSearchPanelMode(Mode.Default);
      history.push(`${replaceParam(RouteDefinitions.Conversations, ':channelId', channelId)}`);
    },
    [setSearchPanelMode, history],
  );

  const hasNextPage = useMemo(() => {
    if (showUnreadEmptyState) {
      return false;
    }
    if (!channelsData?.channelSearch.channels) {
      return true;
    }
    const { total, channels } = channelsData.channelSearch;
    return total > channels.length;
  }, [channelsData, showUnreadEmptyState]);

  useSubscribeToChannelListChannelsSubscription({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
    onData: async () => {
      let unpinnedChannelsTake = DEFAULT_CHANNEL_LIST_PAGE_SIZE;
      if (channelListVariables) {
        unpinnedChannelsTake = channelListVariables.skip + channelListVariables.take;
      }
      // Race condition where the pinned channels are not yet loaded
      setTimeout(async () => {
        await refetchPinnedChannels();
        await refetchChannels({ skip: 0, take: unpinnedChannelsTake });
      }, 750);
    },
  });

  /**
   * Refresh channel list every 3 minutes at a minimum
   */
  useEffect(() => {
    const THREE_MINUTES_IN_MS = 1000 * 60 * 3;

    const interval = setInterval(() => {
      if (refetchChannels) refetchChannels();
    }, THREE_MINUTES_IN_MS);
    return (): void => clearInterval(interval);
  }, [refetchChannels]);

  const handleSearchToggle = useCallback(
    (isSearching) => {
      setChannelSearchInput({});
      setIsSearching(isSearching);
      if (isSearching) {
        trackEvent(ConversationEvents.ChannelSearchListOpened, { isEnhancedConversationsSearch: true });
      }
    },
    [trackEvent],
  );

  const handleFilterSelect = useCallback(
    (filterSelection: Partial<IChannelListFilterSelection>, ignoreFilterKeys?: (keyof IChannelListFilter)[]) => {
      if (!currentClinicId || !userId) return;

      let showArchived = !!filterSelection.filters?.showArchived;
      if (!showArchived && filterSelection.filters?.channelStatusIds) {
        const hasArchivedStatus = filterSelection.filters.channelStatusIds.some((id) => {
          const channelStatus = channelStatusesData?.findManyChannelStatus?.find((status) => status.id === id);
          return channelStatus && channelStatus.channelStatusAction !== ChannelStatusAction.Active;
        });
        if (hasArchivedStatus) {
          showArchived = true;
        }
      }

      setChannelListFilterSelections((value) => {
        return {
          ...value,
          [clinicKey]: {
            ...value?.[clinicKey],
            [userKey]: {
              ...value?.[clinicKey]?.[userKey],
              ...filterSelection,
              filters: {
                ...value?.[clinicKey]?.[userKey]?.filters,
                showArchived, // Ensure we hide archived conversations by default
                ...omit(filterSelection.filters, ignoreFilterKeys || []),
              },
            },
          },
        };
      });
    },
    [currentClinicId, clinicKey, userId, userKey, channelStatusesData, setChannelListFilterSelections],
  );

  const handleShowArchivedToggle = useCallback(() => {
    const filters = currentChannelFilterSelection.filters;
    const showArchived = !filters.showArchived;
    let channelStatusIds = filters.channelStatusIds.slice();

    if (!showArchived && filters.channelStatusIds.length) {
      const archivedStatuses = new Set(
        filters.channelStatusIds.filter((id) => {
          const channelStatus = channelStatusesData?.findManyChannelStatus?.find((status) => status.id === id);
          return channelStatus && channelStatus.channelStatusAction !== ChannelStatusAction.Active;
        }),
      );
      if (archivedStatuses.size) {
        channelStatusIds = channelStatusIds.filter((id) => !archivedStatuses.has(id));
      }
    }

    handleFilterSelect({
      ...defaultChannelListFilterSelection,
      filters: {
        ...currentChannelFilterSelection.filters,
        showArchived,
        channelStatusIds,
      },
    });
  }, [channelStatusesData?.findManyChannelStatus, currentChannelFilterSelection, handleFilterSelect]);

  /**
   * Immediately apply and clear any old filters for this clinic user
   */
  useEffect(() => {
    if (JSON.stringify(oldChannelListFilter) !== JSON.stringify(defaultChannelListFilter)) {
      handleFilterSelect({
        id: '',
        name: '',
        isDeleted: false,
        filterSelectionType: FilterSelectionType.Private,
        filters: oldChannelListFilter,
      });
      setChannelListFilters((value) => omit(value, `${clinicKey}.${userKey}`));
    }
  }, [clinicKey, userKey, oldChannelListFilter, handleFilterSelect, setChannelListFilters]);

  const clearChannelFilter = useCallback(
    (source?: string, ignoreFilterKeys?: (keyof IChannelListFilter)[]): void => {
      handleFilterSelect(defaultChannelListFilterSelection, [...(ignoreFilterKeys || []), 'showArchived']);
      setFilterPopoverKey(Date.now());

      if (source) {
        trackEvent(ConversationEvents.FilterRemoved, { source });
      }
    },
    [handleFilterSelect, trackEvent],
  );

  const handleChannelListViewSelect = useCallback(
    (filterType: ChannelListFilterType): void => {
      handleFilterSelect({
        ...currentChannelFilterSelection,
        filters: {
          ...currentChannelFilterSelection.filters,
          unread: filterType === ChannelListFilterType.Unread,
        },
      });
      if (filterType === ChannelListFilterType.Unread) {
        history.push(RouteBasePaths.Conversations);
      }
    },
    [currentChannelFilterSelection, history, handleFilterSelect],
  );

  const isFilterApplied = useMemo(() => !isEqual(channelListFilter, defaultChannelListFilter), [channelListFilter]);

  const handleChannelSearchInputChange = useCallback(
    (updates: ChannelSearchInputUpdates): void => {
      const newChannelSearchInput = omitBy(updates, isEmpty);
      setChannelSearchInput(newChannelSearchInput);
      const inputCount = Object.values(newChannelSearchInput).filter((v) => !!v).length;
      trackEvent(ConversationEvents.ChannelSearchList, {
        isFilterApplied,
        showArchived: channelListFilter.showArchived,
        inputCount,
      });
    },
    [channelListFilter, isFilterApplied, trackEvent],
  );

  const searchResultsCount = useMemo(() => {
    if (channelListFilter.unread && !unreadChannelIds?.length) {
      return 0;
    }
    return channelsData?.channelSearch.total || 0;
  }, [channelsData, channelListFilter, unreadChannelIds]);

  const showSearchResultsCount = useMemo(() => {
    const { petParentNames, petNames, messageBodies } = channelSearchInput;
    return !isLoadingChannelList && (!!petParentNames || !!petNames || !!messageBodies);
  }, [isLoadingChannelList, channelSearchInput]);

  const showEmptyState = useMemo(() => {
    if (isLoadingChannelList || !channelsData?.channelSearch) {
      return false;
    }
    const {
      channelSearch: { total, channels },
    } = channelsData;
    return !isLoadingChannelList && ((showSearchResultsCount && total === 0) || (channels && channels?.length === 0));
  }, [isLoadingChannelList, showSearchResultsCount, channelsData]);

  return {
    channelSearchInput,
    channelListFilter,
    hasLoadedInitialChannelList,
    hasNextPage,
    refetchChannels,
    unpinnedChannels,
    isLoadingChannelList,
    unreadChannelIds,
    showUnreadEmptyState,
    clearChannelFilter,
    isSearching,
    currentChannelFilterSelection,
    isFilterApplied,
    handleSearchToggle,
    handleChannelSearchInputChange,
    handleFilterSelect,
    handleChannelListViewSelect,
    handleShowArchivedToggle,
    channelId,
    unreadMessageCountByChannel,
    handleChannelSelect,
    searchResultsCount,
    showSearchResultsCount,
    showEmptyState,
    pinnedChannels,
    filterPopoverKey,
    teletriageConversationCenter,
    isTeletriageConversationCenterOpen,
    onTeletriageConversationCenterOpen,
    onTeletriageConversationCenterClose,
  };
};
